/**
 * Nimike-picker laskurivilomakkeeseen.
 *
 * - Avaa modaali, jossa lista käyttäjän tallentamista nimikkeistä
 * - Haku + kategoriasuodatus
 * - "Valitse" lisää nimikkeen riville
 * - "Tallenna nykyinen rivi nimikkeeksi" -nappi tallentaa nopeasti uuden nimikkeen
 */
import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Search, Package, Plus, Loader2, Bookmark, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const eur = (v) => new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" })
  .format(Number(v || 0));

export default function InvoiceItemPicker({ open, onClose, onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = [];
      if (q) params.push(`q=${encodeURIComponent(q)}`);
      if (category) params.push(`category=${encodeURIComponent(category)}`);
      const url = "/invoice-items" + (params.length ? `?${params.join("&")}` : "");
      const [a, c] = await Promise.all([
        api.get(url),
        api.get("/invoice-items/categories"),
      ]);
      setItems(a.data);
      setCategories(c.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // search debounce-likely
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category]);

  const pick = (it) => {
    onSelect({
      template_id: it.id,
      description: it.description || it.name,
      name: it.name,
      unit: it.unit || "kpl",
      unit_price: it.unit_price || 0,
      vat_percent: it.vat_percent ?? 25.5,
      quantity: it.default_quantity || 1,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Valitse nimikkeestä</DialogTitle>
          <DialogDescription>
            Valitse tallentamasi laskurivi. Voit luoda ja muokata nimikkeitä
            <a href="/invoicing/items" target="_blank" rel="noopener" className="text-amber-300 underline mx-1 inline-flex items-center gap-1">
              Nimikekirjastossa <ExternalLink className="w-3 h-3" />
            </a>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 items-center">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Hae nimellä, kuvauksella tai koodilla..."
              className="pl-10"
              data-testid="item-picker-search"
              autoFocus
            />
          </div>
          {categories.length > 0 && (
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
              data-testid="item-picker-category"
            >
              <option value="">Kaikki kategoriat</option>
              {categories.map((c) => (
                <option key={c.category} value={c.category}>{c.category} ({c.count})</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="p-8 flex items-center gap-2 text-white/40">
              <Loader2 className="w-5 h-5 animate-spin" /> Ladataan...
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-white/40">
              <Package className="w-10 h-10 mx-auto mb-3 text-white/20" />
              <p className="mb-3">Ei vielä tallennettuja nimikkeitä.</p>
              <Button variant="outline" onClick={() => window.open("/invoicing/items", "_blank")}>
                <Plus className="w-3 h-3 mr-1" /> Luo ensimmäinen
              </Button>
            </div>
          ) : (
            <ul className="space-y-1.5 py-1">
              {items.map((it) => (
                <li key={it.id}>
                  <button
                    onClick={() => pick(it)}
                    className="w-full text-left px-3 py-2.5 rounded-lg bg-white/[0.02] hover:bg-amber-500/10 border border-white/5 hover:border-amber-500/30 transition flex items-center gap-3"
                    data-testid={`item-picker-row-${it.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white font-medium">{it.name}</p>
                        {it.code && (
                          <code className="text-[10px] bg-white/5 text-white/60 px-1.5 rounded">{it.code}</code>
                        )}
                        {it.category && (
                          <span className="text-[10px] bg-amber-500/10 text-amber-200 border border-amber-500/30 px-1.5 rounded">
                            {it.category}
                          </span>
                        )}
                        {it.usage_count > 0 && (
                          <span className="text-[10px] text-white/40">käytetty {it.usage_count}×</span>
                        )}
                      </div>
                      {it.description && (
                        <p className="text-xs text-white/50 truncate mt-0.5">{it.description}</p>
                      )}
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <p className="text-amber-300 font-mono text-sm">{eur(it.unit_price)} <span className="text-white/40 text-[10px]">/ {it.unit}</span></p>
                      <p className="text-[10px] text-violet-300">ALV {it.vat_percent} %</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


/** Inline-painike: "Tallenna nimikkeenä" rivin vieressä. */
export function SaveAsTemplateButton({ row, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", code: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Anna nimi nimikkeelle");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/invoice-items", {
        name: form.name.trim(),
        description: row.description || "",
        unit: row.unit || "kpl",
        unit_price: parseFloat(row.unit_price) || 0,
        vat_percent: parseFloat(row.vat_percent) || 25.5,
        default_quantity: parseFloat(row.quantity) || 1,
        category: form.category || null,
        code: form.code || null,
      });
      toast.success("Nimike tallennettu");
      setOpen(false);
      setForm({ name: "", category: "", code: "" });
      onSaved?.(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => {
          setForm({ name: row.description || "", category: "", code: "" });
          setOpen(true);
        }}
        className="text-amber-300 hover:text-amber-200"
        title="Tallenna nimikkeeksi"
        data-testid="item-save-as-template"
      >
        <Bookmark className="w-4 h-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Tallenna nimikkeeksi</DialogTitle>
            <DialogDescription>
              Tämä rivi tallennetaan nimikekirjastoon, josta voit valita sen uudelleen muille laskuille.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nimike *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Esim. Asiantuntijatyö, h"
                autoFocus
                data-testid="save-template-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Kategoria</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Palvelut / Tuotteet"
                />
              </div>
              <div>
                <Label className="text-xs">Koodi</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="SKU"
                />
              </div>
            </div>
            <div className="bg-white/[0.03] rounded-lg p-3 text-xs space-y-0.5">
              <p className="text-white/60">Kuvaus: <span className="text-white/90">{row.description || "—"}</span></p>
              <p className="text-white/60">Yksikkö: {row.unit || "kpl"} · Hinta: {eur(row.unit_price)} · ALV {row.vat_percent} %</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)} className="flex-1">Peruuta</Button>
              <Button onClick={save} disabled={saving} className="btn-luxe flex-1" data-testid="save-template-confirm">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Tallenna
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
